import { useCallback, useEffect, useRef, useState } from "react";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { useChapterMemoPolling } from "./useChapterMemoPolling";

type TGenerationStatus =
  | "pending"
  | "processing"
  | "streaming"
  | "ready"
  | "processed"
  | "error"
  | "errorSeen"
  | "cancelled";

export type TSnippetStatus = {
  slug: string;
  status: "done" | "pending" | "error" | "processing";
};

export type TChapterPhase = "documentParsing" | "chapterGeneration" | "chapterWaiting" | "chapterConfiguration";

export type TChapterStatusEntry = {
  phase: TChapterPhase;
  message: string;
  snippetsStatus: TSnippetStatus[];
};

export type TChapterStatusMap = Record<string, TChapterStatusEntry>;

type TProjectSetupStatus = {
  status: TGenerationStatus;
  id: number;
  result?: string;
  statusMessage: string;
};

type TActiveTaskResponse = {
  active: boolean;
  generationId?: number;
  type?: string;
};

function parseChapterStatusMap(statusMessage: string): TChapterStatusMap | null {
  if (!statusMessage) return null;
  try {
    const parsed = JSON.parse(statusMessage);
    if (typeof parsed === "object" && parsed !== null) {
      const result: TChapterStatusMap = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (value && typeof value === "object" && "message" in value) {
          result[key] = value as TChapterStatusEntry;
        }
      }
      return Object.keys(result).length > 0 ? result : null;
    }
    return null;
  } catch {
    return null;
  }
}

function getProgressMessageFromMap(
  chapterStatusMap: TChapterStatusMap | null,
  chapterId: number | null
): string | null {
  if (!chapterStatusMap || !chapterId) return null;
  const chapterIdStr = chapterId.toString();
  const entry = chapterStatusMap[chapterIdStr];
  return entry?.message ?? null;
}

export function useProjectSetupPolling(
  projectId: string,
  activeChapterId: number | null,
  enabled = true,
  onComplete?: () => void
) {
  const { setSetupTask, clearSetupTask, updateTaskStatus } = useProjectSetupStore();

  const taskId = useProjectSetupStore((state) => state.activeSetupTasks[projectId] || null);
  const storedStatus = useProjectSetupStore((state) => state.taskStatuses[projectId] || null);

  const [isPolling, setIsPolling] = useState(false);
  const [isPollingInitialized, setIsPollingInitialized] = useState(false);
  const [status, setStatus] = useState<TGenerationStatus | null>(storedStatus);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [progressMessage, setProgressMessage] = useState("Setting up project...");
  const [chapterStatusMap, setChapterStatusMap] = useState<TChapterStatusMap | null>(null);
  const hasCheckedForActiveTask = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const chapterMemoPolling = useChapterMemoPolling(projectId, isProcessing, 3000);

  useEffect(() => {
    if (storedStatus) {
      setStatus(storedStatus);
    }
  }, [storedStatus]);

  useEffect(() => {
    const message = getProgressMessageFromMap(chapterStatusMap, activeChapterId);
    if (message) {
      setProgressMessage(message);
    }
  }, [activeChapterId, chapterStatusMap]);

  const stopPolling = () => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    setIsPolling(false);
  };

  const checkForActiveTask = useCallback(async () => {
    if (!projectId || !enabled || hasCheckedForActiveTask.current) {
      return;
    }

    hasCheckedForActiveTask.current = true;

    try {
      const response = await $api.get<TActiveTaskResponse>("/chat/check-active", {
        params: { projectId, type: "projectSetup" }
      });

      if (response.data.active && response.data.generationId) {
        setSetupTask(projectId, response.data.generationId);
      } else {
        setIsPolling(false);
        setIsPollingInitialized(true);
      }
    } catch (error) {
      console.error("Error checking for active setup task:", error);
    }
  }, [projectId, enabled, setSetupTask]);

  const poll = async () => {
    if (!taskId || !enabled) {
      stopPolling();
      return;
    }

    try {
      const response = await $api.get<TProjectSetupStatus>(`/generation-tasks/${taskId}/status`);
      setIsPollingInitialized(true);

      const taskStatus = response.data.status;
      const statusMessage = response.data.statusMessage;

      setStatus(taskStatus);
      updateTaskStatus(projectId, taskStatus);

      const parsedStatusMap = parseChapterStatusMap(statusMessage);
      setChapterStatusMap(parsedStatusMap);
      const dynamicMessage = getProgressMessageFromMap(parsedStatusMap, activeChapterId);

      switch (taskStatus) {
        case "pending":
          setProgressMessage(dynamicMessage ?? "");
          setIsPolling(true);
          pollingTimeoutRef.current = setTimeout(poll, 7000);
          break;

        case "processing":
          setProgressMessage(dynamicMessage ?? "");
          setIsProcessing(true);
          setIsPolling(true);
          pollingTimeoutRef.current = setTimeout(poll, 7000);
          break;

        case "processed":
        case "ready":
          setProgressMessage("Project setup complete!");
          setIsProcessing(false);
          clearSetupTask(projectId);
          stopPolling();
          if (onComplete) {
            onComplete();
          }
          break;

        case "error":
        case "errorSeen":
          setProgressMessage("Project setup failed");
          setIsProcessing(false);
          clearSetupTask(projectId);
          stopPolling();
          toast.error("Project setup failed. You can still use the project.");
          break;

        case "cancelled":
          setProgressMessage("Project setup cancelled");
          setIsProcessing(false);
          clearSetupTask(projectId);
          stopPolling();
          break;

        default:
          setIsProcessing(false);
          stopPolling();
      }
    } catch (error) {
      console.error("Error polling project setup status:", error);
      stopPolling();
    }
  };

  useEffect(() => {
    checkForActiveTask();
  }, [checkForActiveTask]);

  useEffect(() => {
    if (enabled && taskId && !isPolling) {
      poll();
    }

    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, enabled]);

  return {
    isPolling,
    status,
    progressMessage,
    chapterStatusMap,
    stopPolling,
    chapterMemoPolling,
    isPollingInitialized
  };
}
